import { useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  FormControlLabel,
  Switch,
  Stack,
  Typography,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "i18n-fe";
import { filterVisibleClasses } from "score-common";
import { useClassesForShow } from "../hooks/useClassesForShow";
import { classNumberLabel } from "../util/classDisplay";
import SplitClassChip from "./SplitClassChip";

// #694 — class-search modal, reachable via the "Find a class" pill in
// SessionHeader (#718 moved it there from a top-left hamburger menu, since
// SessionHeader is always-present chrome). "My classes" reuses
// useClassesForShow(showId) unmodified (the same assignment-filtered,
// time-ordered list the day sheet renders, #627/#632); "all classes" uses the
// same hook's `{ all: true }` mode added alongside it, so there is exactly one
// definition of "mine" in the app (shared with #695's day-sheet list).
//
// #883 — this IS the "manual-add CNO" class picker (a scribe resolves an
// off-daysheet rider to a class by number here). A ride-out/split FVD section
// carries the SAME classNumber/className as its base class, so a search can
// return two identically-labeled results; a scribe scoring by number alone
// silently landed on the split (CDS prod, CNO 462). Split results get a
// "Split/RO" chip and useClassesForShow's tiebreak now sorts the base class
// first (classDisplay#compareByClassNumber), so the two are never
// indistinguishable and the base is never buried under its split.
export default function ClassSearchModal({ open, onClose, showId }) {
  const { t } = useTranslation("score");
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [showAll, setShowAll] = useState(false);

  const { data: myClasses } = useClassesForShow(showId);
  const { data: allClasses } = useClassesForShow(showId, { all: true });
  const source = showAll ? allClasses : myClasses;

  const results = useMemo(() => {
    // #1117 — hidden classes are not offered in the class search either.
    const list = filterVisibleClasses(source || []);
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((c) => classNumberLabel(c).toLowerCase().includes(q));
  }, [source, query]);

  const handleClose = () => {
    setQuery("");
    onClose();
  };

  const handleSelect = (c) => {
    handleClose();
    navigate(`/classes/${c.id}`);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      data-testid="class-search-modal"
    >
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
      >
        {t("classSearch.title")}
        <IconButton
          onClick={handleClose}
          aria-label={t("classSearch.close")}
          data-testid="class-search-close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <FormControlLabel
            control={
              <Switch
                data-testid="class-search-scope-switch"
                checked={showAll}
                onChange={(e) => setShowAll(e.target.checked)}
              />
            }
            label={showAll ? t("classSearch.allClasses") : t("classSearch.myClasses")}
          />
          <TextField
            autoFocus
            fullWidth
            label={t("classSearch.searchLabel")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            slotProps={{ htmlInput: { "data-testid": "class-search-input" } }}
          />
          <List data-testid="class-search-results" sx={{ maxHeight: 360, overflowY: "auto" }}>
            {results.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                {t("classSearch.empty")}
              </Typography>
            )}
            {results.map((c) => (
              <ListItemButton
                key={c.id}
                data-testid="class-search-result"
                onClick={() => handleSelect(c)}
              >
                <ListItemText primary={classNumberLabel(c)} />
                {/* #1761 — now the shared SplitClassChip (identical chip,
                    same testid); this modal is no longer the only surface
                    that labels a split section. */}
                <SplitClassChip
                  klass={c}
                  testId="class-search-split-chip"
                  sx={{ ml: 1 }}
                />
              </ListItemButton>
            ))}
          </List>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
